/// <reference lib="webworker" />

import {
  AddHistoryItemApiAddHistoryItemCommand,
  addHistoryItem,
} from "@opendonationassistant/history-service";
import { reportError, reportStarted } from "../worker-status";
import axios from "axios";
import type { TokenDto } from "../systems";
import { log } from "./log";

// Endpoints come from the build-time environment, with a fallback to the
// production gateway.
const API_ENDPOINT =
  process.env.REACT_APP_API_ENDPOINT ?? "https://api.oda.digital";
const HISTORY_API_ENDPOINT =
  process.env.REACT_APP_HISTORY_API_ENDPOINT ?? "https://api.oda.digital";
const DONATIONALERTS_API_URL = `${API_ENDPOINT}/donationalerts`;
const CENTRIFUGO_WEBSOCKET_URL =
  "wss://centrifugo.donationalerts.com/connection/websocket";
const CENTRIFUGO_SUBSCRIBE_URL =
  "https://www.donationalerts.com/api/v1/centrifuge/subscribe";
const API_TIMEOUT_MS = 15000;

let connectedTokens: string[] = [];
const activeSockets = new Set<WebSocket>();

// ── DonationAlerts data shapes ─────────────────────────────────────

interface DonationAlertsSettings {
  triggerAlerts: boolean;
  triggerReel: boolean;
  triggerDonaton: boolean;
  countInTop: boolean;
  addToGoal: boolean;
}

interface DonationAlertsPayment {
  id: string;
  username: string;
  message: string;
  amount_in_user_currency: number;
}

/**
 * Centrifugo push message: either the auth reply (id: 1) or a
 * donation event published on the subscribed channel.
 */
interface DonationAlertsMessage {
  id?: number;
  result?: {
    client?: string;
    channel?: string;
    data?: {
      data?: DonationAlertsPayment;
    };
  };
}

// ── DonationAlerts API helpers ─────────────────────────────────────

/**
 * Exchange the DonationAlerts access token for the user ID and the
 * Centrifugo connection token.
 */
async function getSocketConnectionInfo(
  daToken: string,
): Promise<{ userId: string; centrifugoToken: string }> {
  const response = await axios.get(DONATIONALERTS_API_URL, {
    headers: { Authorization: `Bearer ${daToken}` },
    timeout: API_TIMEOUT_MS,
  });
  return {
    userId: response.data.data.id,
    centrifugoToken: response.data.data.socket_connection_token,
  };
}

/**
 * Ask the DonationAlerts backend for a channel subscription token
 * bound to the client id returned by the Centrifugo auth reply.
 */
async function subscribeToChannel(
  daToken: string,
  channel: string,
  client: string,
): Promise<string> {
  const response = await axios.post(
    CENTRIFUGO_SUBSCRIBE_URL,
    { channels: [channel], client },
    {
      headers: { Authorization: `Bearer ${daToken}` },
      timeout: API_TIMEOUT_MS,
    },
  );
  return response.data.channels[0].token;
}

// ── WebSocket message handling ─────────────────────────────────────

function handleWebSocketMessage(
  odaToken: string,
  recipientId: string,
  daToken: string,
  settings: DonationAlertsSettings,
  channel: string,
  raw: string,
  socket: WebSocket,
): void {
  const message: DonationAlertsMessage = JSON.parse(raw);

  if (message.id === 1) {
    const client = message.result?.client;
    if (!client) {
      log("ERROR", "DonationAlerts Centrifugo auth reply missing client id");
      reportError(odaToken, "DonationAlerts", "Centrifugo auth reply missing client id");
      return;
    }
    subscribeToChannel(daToken, channel, client)
      .then((channelToken) => {
        log("INFO", `DonationAlerts subscribed to channel ${channel}`);
        socket.send(
          JSON.stringify({
            params: {
              channel,
              token: channelToken,
            },
            method: 1,
            id: 2,
          }),
        );
      })
      .catch((err) => {
        log("ERROR", "Failed to subscribe to DonationAlerts channel:", err);
        reportError(odaToken, "DonationAlerts", `failed to subscribe to channel: ${err}`);
      });
    return;
  }

  if (message.result?.channel === channel && message.result?.data?.data) {
    handleDonation(odaToken, recipientId, settings, message.result.data.data);
  }
}

function handleDonation(
  odaToken: string,
  recipientId: string,
  settings: DonationAlertsSettings,
  payment: DonationAlertsPayment,
): void {
  log("INFO",
    `DonationAlerts donation: ${payment.amount_in_user_currency} RUB from ${payment.username}`,
  );

  const command: AddHistoryItemApiAddHistoryItemCommand = {
    recipientId,
    amount: {
      minor: 0,
      major: payment.amount_in_user_currency,
      currency: "RUB",
    },
    nickname: payment.username,
    message: payment.message,
    triggerAlert: settings.triggerAlerts,
    triggerReel: settings.triggerReel,
    triggerDonaton: settings.triggerDonaton,
    goals: [],
    addToTop: settings.countInTop,
    addToGoal: settings.addToGoal,
    paymentId: payment.id,
    system: "DonationAlerts",
    event: "payment",
    authorizationTimestamp: new Date().toISOString(),
  };

  addHistoryItem({
    baseURL: HISTORY_API_ENDPOINT,
    body: command,
    headers: {
      Authorization: `Bearer ${odaToken}`,
    },
  })
    .then(() =>
      log("INFO",
        `DonationAlerts donation persisted to history [${payment.amount_in_user_currency} RUB]`,
      ),
    )
    .catch((err) => {
      log("ERROR",
        "Failed to persist DonationAlerts donation to history:",
        err,
      );
      reportError(odaToken, "DonationAlerts", `failed to persist donation to history: ${err}`);
    });
}

// ── WebSocket lifecycle ─────────────────────────────────────────────

function startWebSocketClient(
  odaToken: string,
  recipientId: string,
  daToken: string,
  settings: DonationAlertsSettings,
  userId: string,
  centrifugoToken: string,
): void {
  log("INFO", "Starting DonationAlerts Centrifugo WebSocket connection");
  const socket = new WebSocket(CENTRIFUGO_WEBSOCKET_URL);
  const channel = `$alerts:donation_${userId}`;
  activeSockets.add(socket);

  socket.addEventListener("error", (err) => {
    log("ERROR", "DonationAlerts WebSocket error:", err);
    reportError(odaToken, "DonationAlerts", `WebSocket error: ${err}`);
  });

  socket.addEventListener("open", () => {
    log("INFO", "DonationAlerts Centrifugo WebSocket opened");
    reportStarted(odaToken, "DonationAlerts");
    socket.send(
      JSON.stringify({
        params: {
          token: centrifugoToken,
        },
        id: 1,
      }),
    );
  });

  socket.addEventListener("message", (event) => {
    handleWebSocketMessage(
      odaToken,
      recipientId,
      daToken,
      settings,
      channel,
      event.data as string,
      socket,
    );
  });

  socket.addEventListener("close", (event) => {
    const wasRegistered = activeSockets.delete(socket);
    if (event.code !== 1000) {
      reportError(
        odaToken,
        "DonationAlerts",
        `WebSocket closed with code ${event.code}${event.reason ? `: ${event.reason}` : ""}`,
      );
    }
    if (!wasRegistered) return; // Closed by deregister — do not reconnect.
    log("INFO",
      "DonationAlerts Centrifugo WebSocket closed. Reconnection attempt in 1s",
    );
    setTimeout(() => {
      startConnection(odaToken, recipientId, daToken, settings);
    }, 1000);
  });
}

function startConnection(
  odaToken: string,
  recipientId: string,
  daToken: string,
  settings: DonationAlertsSettings,
): void {
  getSocketConnectionInfo(daToken)
    .then(({ userId, centrifugoToken }) => {
      startWebSocketClient(
        odaToken,
        recipientId,
        daToken,
        settings,
        userId,
        centrifugoToken,
      );
    })
    .catch((err) => {
      log("ERROR",
        "Failed to get DonationAlerts socket connection info:",
        err,
      );
      reportError(odaToken, "DonationAlerts", err);
    });
}

// ── Registration (called from logger-worker) ────────────────────────

export function register(
  token: string,
  recipientId: string,
  tokens: TokenDto[] | null,
): void {
  if (!tokens) {
    reportError(token, "DonationAlerts", "Failed to fetch recipient tokens");
    return;
  }
  tokens
    .filter((t) => t.system === "DonationAlerts")
    .filter((t) => t.enabled)
    .filter((t) => !connectedTokens.includes(t.id))
    .forEach((t) => {
      log("INFO", `add donationalerts handler for ${t.id}`);
      connectedTokens.push(t.id);

      startConnection(
        token,
        recipientId,
        t.token,
        t.settings as unknown as DonationAlertsSettings,
      );
    });
}

export function deregister(): void {
  log("INFO", { connected: connectedTokens }, "remove donationalerts listener");
  activeSockets.forEach((socket) => {
    activeSockets.delete(socket);
    socket.close(1000, "deregistered");
  });
  connectedTokens = [];
}
